/**
 * MoodLineChart.tsx — S2–S5 session mood trend line (VER-76 dependency).
 *
 * DELIBERATELY SEPARATE from WemwbsBarChart per JK spec:
 *   - Different scale (mood 1–5 vs WEMWBS 14–70)
 *   - Different data source (VER-76 session mood vs VER-55 SurveyMonkey)
 *   - Rendered in its own card so they are visually distinct and cannot be
 *     conflated by practitioners reading the screen
 *
 * ANONYMITY: this component plots whatever series it is given and enforces nothing — the caller
 *   owns the disclosure decision. On the group Impact tab that series is a per-session average and
 *   carries no participant identifiers; on the participant Impact tab (VER-118) it is deliberately
 *   one named person's own readings, shown to a viewer already permitted to see them.
 *   A caller surfacing individual data MUST pass a `caption` saying so — the default caption
 *   describes the group case, and left unchanged it would tell a reader the opposite of the truth.
 *
 * TODO [VER-76]: Replace MOCK_MOOD_SERIES with real session mood data
 *   once VER-76 (session mood capture) is live. This component is wired
 *   to accept MoodPoint[] as a prop so the swap is a one-liner.
 */

import React, { FC } from "react";
import { Alert, Box, Typography, useTheme } from "@mui/material";
import {
    VictoryAxis,
    VictoryChart,
    VictoryLine,
    VictoryScatter,
    VictoryTheme,
    VictoryTooltip,
    VictoryVoronoiContainer,
} from "victory";
import { MoodPoint } from "./impact.mock";
import { verbalTheme } from "../../layout/themes/verbal.theme";

type MoodLineChartProps = {
    series: MoodPoint[];
    /**
     * Heading and caption (VER-118).
     *
     * These are overridable because the same chart now serves two surfaces with genuinely
     * different disclosure. The group view shows an average and truthfully says individual
     * responses are not shown; the participant Impact tab shows that ONE participant's own
     * readings to a viewer already permitted to see them. Printing the group caption there
     * would be a false anonymity claim on a screen that is disclosing an individual — so the
     * wording travels with the data, not with the component.
     */
    title?: string;
    caption?: string;
};

const GROUP_CAPTION =
    "Group average mood rating per session (1 = very low, 5 = very high). Individual responses are not shown.";

export const MoodLineChart: FC<MoodLineChartProps> = ({
    series,
    title = "Session Mood Trend (S2–S5)",
    caption = GROUP_CAPTION,
}) => {
    const theme = useTheme();

    const colorMood = theme.palette.warning.main; // amber — distinct from WEMWBS teal/blue

    const chartData = series.map((p) => ({
        x: p.sessionLabel,
        y: p.groupMoodAvg,
        label: `${p.sessionLabel}: ${p.groupMoodAvg.toFixed(1)} / 5`,
    }));

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {title}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
                {caption}
            </Typography>

            {/* Visual separator callout — reinforces intentional separation from WEMWBS */}
            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                Mood is captured in-session and is independent of the survey scores above. It is a single holistic
                rating, not a validated scale.
            </Alert>

            <VictoryChart
                theme={VictoryTheme.material}
                containerComponent={
                    <VictoryVoronoiContainer
                        labels={({ datum }: { datum: { label: string } }) => datum.label}
                        labelComponent={
                            <VictoryTooltip
                                cornerRadius={() => 4}
                                flyoutPadding={6}
                                flyoutStyle={{ fill: "white", stroke: "#e3e3e3" }}
                                style={{ fontSize: 9, fontFamily: verbalTheme.typography.fontFamily }}
                                constrainToVisibleArea
                            />
                        }
                    />
                }
                width={620}
                height={200}
                padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                domain={{ y: [1, 5] }}
            >
                <VictoryAxis
                    theme={VictoryTheme.material}
                    style={{
                        tickLabels: { fontSize: 9, fontFamily: verbalTheme.typography.fontFamily },
                    }}
                />
                <VictoryAxis
                    dependentAxis
                    theme={VictoryTheme.material}
                    tickValues={[1, 2, 3, 4, 5]}
                    tickFormat={(t: number) => `${t}`}
                    label="Mood"
                    style={{
                        axisLabel: { fontSize: 9, padding: 36, fontFamily: verbalTheme.typography.fontFamily },
                        tickLabels: { fontSize: 9, fontFamily: verbalTheme.typography.fontFamily },
                    }}
                />
                <VictoryLine
                    data={chartData}
                    style={{
                        data: { stroke: colorMood, strokeWidth: 2 },
                    }}
                    interpolation="monotoneX"
                />
                <VictoryScatter data={chartData} size={5} style={{ data: { fill: colorMood } }} />
            </VictoryChart>
        </Box>
    );
};
